import { useState } from 'react';
import { useGame } from '../state/gameContext';
import type { MockPlayer } from '../data/mockData';
import { PlayerAvatar } from './PlayerAvatar';
import { MaterialIcon } from './MaterialIcon';

interface ScoreboardRowProps {
  player: MockPlayer;
}

export function ScoreboardRow({ player }: ScoreboardRowProps) {
  const isJudge = player.isJudge;
  const isDisconnected = player.isConnected === false;

  return (
    <div
      className={[
        'px-4 py-3 rounded-lg flex items-center justify-between gap-3 transition-all',
        isJudge
          ? 'bg-primary/10 border border-primary/30 text-primary'
          : isDisconnected
            ? 'text-on-surface-variant opacity-60'
            : 'text-on-surface-variant hover:bg-surface-variant/50',
      ].join(' ')}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="relative">
          <PlayerAvatar
            initials={player.initials}
            gradient={player.gradient}
            size="md"
            alt={`${player.name} avatar`}
            className={isDisconnected ? 'grayscale' : ''}
          />
          {isJudge && (
            <div className="absolute -bottom-1 -right-1 bg-secondary text-[10px] font-bold px-1 rounded border border-on-secondary-container">
              JUDGE
            </div>
          )}
        </div>
        <div>
          <p className="font-label-bold text-label-bold break-words">{player.name}</p>
          {isJudge && <p className="text-[10px] opacity-70">The Judge</p>}
          {isDisconnected && <p className="text-[10px]">Disconnected</p>}
          {!isJudge && !isDisconnected && player.isReady && (
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
              <p className="text-[10px]">Active</p>
            </div>
          )}
        </div>
      </div>
      <span className="font-bold">{player.score}</span>
    </div>
  );
}

interface ScoreboardProps {
  players: MockPlayer[];
  title?: string;
}

export function Scoreboard({ players, title = 'Scoreboard' }: ScoreboardProps) {
  const { state } = useGame();
  const [inviteStatus, setInviteStatus] = useState('Invite Friends');
  const copyInvite = async () => {
    if (!state.roomCode) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/join?code=${state.roomCode}`);
      setInviteStatus('Invite link copied');
    } catch { setInviteStatus(`Share room code: ${state.roomCode}`); }
  };
  return (
    <aside className="fixed left-0 top-16 h-[calc(100vh-4rem)] z-40 flex flex-col p-stack-md w-72 bg-surface-container/95 border-r border-white/10 shadow-xl hidden lg:flex">
      <div className="mb-stack-lg">
        <h2 className="font-headline-md text-headline-md text-secondary mb-1">{title}</h2>
        <p className="text-on-surface-variant text-label-sm">Top players this match</p>
      </div>
      <div className="flex flex-col gap-3 overflow-y-auto custom-scrollbar pr-2 flex-1">
        {players.map((player) => (
          <ScoreboardRow key={player.id} player={player} />
        ))}
      </div>
      <button
        type="button"
        onClick={copyInvite}
        aria-live="polite"
        className="mt-auto w-full bg-primary text-on-primary py-3 rounded-lg font-label-bold hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2"
      >
        <MaterialIcon name="person_add" />
        {inviteStatus}
      </button>
    </aside>
  );
}
